<template>
    <div class="shipment-items-table">
        <div class="shipment-items-table__header">
            <h3 class="shipment-items-table__title">
                {{ $t('pages.shipments.items') }}
            </h3>
            <div class="shipment-items-table__actions">
                <v-btn
                    v-if="selectedItems.length > 0"
                    color="primary"
                    small
                    :loading="isClosing"
                    @click="$emit('close-selected')"
                >
                    {{ $t('pages.shipments.closeSelected') }}
                </v-btn>
                <v-btn
                    color="primary"
                    small
                    :loading="isClosing"
                    :disabled="!hasUnclosedItems"
                    @click="$emit('close-all')"
                >
                    {{ $t('pages.shipments.closeAll') }}
                </v-btn>
                <v-btn
                    v-if="hasReturnableSelectedItems"
                    color="warning"
                    small
                    :loading="isReturning"
                    @click="$emit('return-selected')"
                >
                    {{ $t('pages.shipments.returnSelected') }}
                </v-btn>
                <v-btn
                    color="warning"
                    small
                    :loading="isReturning"
                    :disabled="!hasUnreturnedItems"
                    @click="$emit('return-all')"
                >
                    {{ $t('pages.shipments.returnAll') }}
                </v-btn>
            </div>
        </div>
        <div class="shipment-items-table__table-wrapper">
            <v-data-table
                :value="localSelectedItems"
                :headers="itemHeaders"
                :items="itemsWithKeys"
                :items-per-page="-1"
                :item-disabled="isItemDisabled"
                hide-default-footer
                dense
                show-select
                item-key="uniqueKey"
                class="shipment-items-table__table"
                @input="onSelectionChange"
            >
                <!--    eslint-disable-next-line -->
                <template #item.wig="{ item }">
                    {{ item.wig?.barcode || item.wig_id || '-' }}
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.client="{ item }">
                    <span class="nowrap-cell-tablet">{{ item.wig?.client || '-' }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.price="{ item }">
                    <span class="nowrap-cell">{{ formatPrice(item.price || 0, item.wig.price_currency) }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.remaining="{ item }">
                    <span class="nowrap-cell">{{
                        formatPrice(
                            item.remaining || 0,
                            item.wig.price_currency
                        )
                    }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.total_paid="{ item }">
                    <span class="nowrap-cell">{{
                        formatPrice(
                            item.total_paid || 0,
                            item.wig.price_currency
                        )
                    }}</span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.document_number="{ item }">
                    {{ item.document_number || '-' }}
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.document_link="{ item }">
                    <ShipmentDocumentLink
                        :link="item.document_link"
                        button-class="shipment-items-table__document-link"
                        icon-class="bi bi-file-earmark-pdf shipment-items-table__document-icon"
                    />
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.closed_at="{ item }">
                    <span class="nowrap-cell">
                        <DateCell
                            v-if="item.closed_at"
                            :value="item.closed_at"
                            :utc="false"
                        />
                        <span v-if="!item.closed_at">-</span>
                    </span>
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.receipts="{ item }">
                    <ShipmentReceiptsDisplay :receipts="item.receipts" />
                </template>
                <!--    eslint-disable-next-line -->
                <template #item.actions="{ item }">
                    <div class="shipment-items-table__actions-cell">
                        <v-btn
                            v-if="!item.closed_at && !isItemReturned(item)"
                            color="primary"
                            small
                            :loading="
                                isClosing &&
                                closingItemId === getItemId(item)
                            "
                            @click="$emit('close-item', item)"
                        >
                            {{ $t('pages.shipments.close') }}
                        </v-btn>
                        <v-btn
                            v-if="canReturnItem(item)"
                            color="warning"
                            small
                            :loading="
                                isReturning &&
                                returningItemId === getItemId(item)
                            "
                            @click="$emit('return-item', item)"
                        >
                            {{ $t('pages.shipments.return') }}
                        </v-btn>
                        <span
                            v-if="item.closed_at"
                            class="shipment-items-table__closed-badge"
                        >
                            {{ $t('pages.shipments.closed') }}
                        </span>
                        <span
                            v-if="isItemReturned(item)"
                            class="shipment-items-table__returned-badge"
                        >
                            {{ $t('pages.shipments.returned') }}
                        </span>
                    </div>
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script>
import shipmentItemsMixin from '@/mixins/shipment-items.mixin'
import DateCell from '@/components/common/cells/date-cell.vue'
import ShipmentDocumentLink from '@/components/shipments/shipment-document-link.vue'
import ShipmentReceiptsDisplay from '@/components/shipments/shipment-receipts-display.vue'
import {
    canReturnItem as canReturnItemUtil,
    isItemReturned as isItemReturnedUtil,
    getItemId as getItemIdUtil,
} from '@/helpers/shipment-items.utils'

export default {
    name: 'ShipmentItemsTable',
    components: {
        DateCell,
        ShipmentDocumentLink,
        ShipmentReceiptsDisplay,
    },
    mixins: [shipmentItemsMixin],
    props: {
        shipment: {
            type: Object,
            required: true,
        },
        selectedItems: {
            type: Array,
            default: () => [],
        },
        isClosing: {
            type: Boolean,
            default: false,
        },
        closingItemId: {
            type: [Number, String],
            default: null,
        },
        isReturning: {
            type: Boolean,
            default: false,
        },
        returningItemId: {
            type: [Number, String],
            default: null,
        },
    },
    emits: [
        'update:selectedItems',
        'close-item',
        'close-selected',
        'close-all',
        'return-item',
        'return-selected',
        'return-all',
    ],
    data: () => ({
        localSelectedItems: [],
    }),
    computed: {
        itemHeaders() {
            return this.getItemHeaders()
        },
        hasUnclosedItems() {
            return (
                this.shipment?.items?.some(
                    (item) => !item.closed_at && !isItemReturnedUtil(item)
                ) || false
            )
        },
        hasUnreturnedItems() {
            return (
                this.shipment?.items?.some((item) => canReturnItemUtil(item)) ||
                false
            )
        },
        hasReturnableSelectedItems() {
            return (
                this.selectedItems?.length > 0 &&
                this.selectedItems.every((item) => canReturnItemUtil(item))
            )
        },
        itemsWithKeys() {
            if (!this.shipment?.items) return []
            return this.shipment.items.map((item, index) => ({
                ...item,
                uniqueKey: getItemIdUtil(item) || `item-${index}`,
            }))
        },
    },
    watch: {
        selectedItems: {
            handler(newVal) {
                this.localSelectedItems = newVal
            },
            immediate: true,
        },
        localSelectedItems: {
            handler(newVal) {
                this.$emit('update:selectedItems', newVal)
            },
        },
    },
    methods: {
        isItemReturned(item) {
            return isItemReturnedUtil(item)
        },
        canReturnItem(item) {
            return canReturnItemUtil(item)
        },
        getItemId(item) {
            return getItemIdUtil(item)
        },
        onSelectionChange(selection) {
            const selectionArray = selection || []
            const filtered = selectionArray.filter((item) => {
                if (!item) return false
                return canReturnItemUtil(item)
            })

            const filteredKeys = filtered
                .map((i) => i.uniqueKey)
                .sort()
                .join(',')
            const currentKeys = this.localSelectedItems
                .map((i) => i.uniqueKey)
                .sort()
                .join(',')

            if (
                filtered.length !== selectionArray.length ||
                filteredKeys !== currentKeys
            ) {
                this.$nextTick(() => {
                    this.localSelectedItems = filtered
                    this.$emit('update:selectedItems', filtered)
                })
                return
            }

            this.$emit('update:selectedItems', filtered)
        },
    },
}
</script>

<style lang="scss" scoped>
.shipment-items-table {
    background: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;

    @include tablet-up {
        padding: 20px;
    }

    &__header {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 16px;
        flex-wrap: wrap;
        gap: 12px;

        @include tablet-up {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
        }
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
    }

    &__actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        width: 100%;

        @include tablet-up {
            width: auto;
            justify-content: flex-start;
        }
    }

    &__table-wrapper {
        flex: 1;
        overflow: auto;
        min-height: 0;
    }

    &__table {
        width: 100%;
    }

    &__document-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        transition: opacity 0.2s;
        padding: 2px;
        border-radius: 4px;
        max-height: 24px;
        max-width: 24px;

        &:hover {
            opacity: 0.7;
        }

        svg {
            display: block;
        }
    }

    &__document-icon {
        width: 18px;
        height: 18px;
        color: red;
    }

    &__closed-badge {
        color: #4caf50;
        font-size: 12px;
        font-weight: 600;
    }

    &__returned-badge {
        color: #ff9800;
        font-size: 12px;
        font-weight: 600;
    }

    &__actions-cell {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
    }
}
</style>
